<template>
  <div class="tarja-wrapper" :class="{ 'tarja-wrapper--ativa': authStore.acessandoComo }">
    <!--
      role="status" precisa existir no DOM antes de qualquer coisa mudar
      DENTRO dela — nunca a região inteira aparecendo/desaparecendo junto
      com o texto. É essa continuidade que faz o leitor de tela anunciar
      tanto a entrada em acesso (F5, que já chega com acessando_como
      preenchido em /auth/me) quanto a confirmação de volta (troca o TEXTO
      desta MESMA região, sem remontá-la).

      O botão fica FORA dela de propósito: role="status" implica
      aria-atomic="true", e ele é descendente. Se o botão morasse aqui
      dentro, cada troca de rótulo ("Voltar a ser eu" -> "Voltando…")
      faria o leitor reler a tarja inteira.
    -->
    <div ref="statusEl" class="tarja-live" role="status" tabindex="-1">
      <span v-if="authStore.acessandoComo" class="texto">
        Você está usando o sistema como <strong>{{ authStore.user?.name }}</strong>,
        conectado como <strong>{{ authStore.nomeDoImpersonador }}</strong>. Esta credencial
        temporária dura 2 horas; passado esse prazo você cairá na tela de login e vai
        precisar entrar novamente.
      </span>
      <span v-else-if="confirmacaoVolta" class="texto sr-only">{{ confirmacaoVolta }}</span>
    </div>

    <button
      v-if="authStore.acessandoComo"
      type="button"
      class="voltar"
      :disabled="voltando"
      @click="voltar"
    >
      {{ voltando ? 'Voltando…' : 'Voltar a ser eu' }}
    </button>

    <!--
      role="alert" também incondicional, mesmo padrão da região de status:
      inserido dinamicamente é reconhecidamente instável em VoiceOver com
      Safari (a pilha desta pessoa), e é justo no caminho de sessão morta
      — sem credencial, sem volta — que o silêncio dói mais.
    -->
    <div
      ref="erroEl"
      class="erro"
      :class="{ 'erro--visivel': !!erro }"
      role="alert"
      tabindex="-1"
    >{{ erro }}</div>
  </div>
</template>

<script lang="ts" setup>
import { nextTick, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { RecargaDeSessaoFalhouError, useAuthStore } from '@/stores/AuthStore';
import { rotaInicialDoPerfil } from '@/utils/rotaInicialDoPerfil';

const authStore = useAuthStore();
const router = useRouter();
const route = useRoute();
const voltando = ref(false);
const erro = ref<string | null>(null);
const confirmacaoVolta = ref<string | null>(null);
const statusEl = ref<HTMLElement | null>(null);
const erroEl = ref<HTMLElement | null>(null);

// A tarja mora no App.vue e nunca desmonta — sem isto, um erro nascido numa
// tela ficaria "grudado" no topo de todas as seguintes até o próximo clique
// ou F5, competindo com o conteúdo real a cada navegação. `route` some em
// teste sem router (useRoute() retorna undefined); a checagem opcional
// evita quebrar o watcher nesse caso.
watch(
  () => route?.fullPath,
  () => {
    erro.value = null;
    confirmacaoVolta.value = null;
  }
);

async function voltar(): Promise<void> {
  if (voltando.value) return;
  voltando.value = true;
  erro.value = null;

  try {
    await authStore.voltarAoMeuUsuario();

    // Navega para o destino correspondente ao perfil de quem voltou.
    // Tratamos o roteamento em bloco interno para que eventuais redirecionamentos
    // ou no-ops de rota não interfiram no sucesso da restauração de credencial.
    const destino = rotaInicialDoPerfil(authStore.activeRole || authStore.getDefaultRole());
    if (router) {
      try {
        const rotaAtual = router.currentRoute?.value;
        const rotaDestino = router.resolve(destino);
        if (rotaAtual && (rotaAtual.path === rotaDestino.path || rotaAtual.name === rotaDestino.name)) {
          router.go(0);
        } else {
          await router.push(destino);
        }
      } catch (navErr) {
        console.warn('Navegação após retorno ao perfil original:', navErr);
      }
    }
    await nextTick();

    confirmacaoVolta.value = `Você voltou a ser ${authStore.user?.name}.`;
    await nextTick();
    statusEl.value?.focus();
  } catch (err) {
    if (err instanceof RecargaDeSessaoFalhouError) {
      erro.value = `${err.message} Faça login novamente.`;
      await nextTick();
      erroEl.value?.focus();
    } else {
      erro.value = 'Não foi possível voltar ao seu usuário agora. Tente novamente.';
    }
  } finally {
    voltando.value = false;
  }
}
</script>

<style scoped>
.tarja-wrapper {
  display: flex;
  flex-wrap: wrap;
  position: relative;
  z-index: 1300;
}

.tarja-wrapper--ativa {
  align-items: center;
  justify-content: center;
  padding: 0.65rem 1.25rem;
  background: var(--tarja-bg, #fef3c7);
  color: var(--tarja-color, #78350f);
  font-weight: 600;
  border-bottom: 2px solid var(--tarja-border, #f59e0b);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

:global(.dark) .tarja-wrapper--ativa {
  --tarja-bg: #451a03;
  --tarja-color: #fef3c7;
  --tarja-border: #b45309;
}

.texto {
  max-width: 60rem;
  line-height: 1.5;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.voltar {
  margin-left: 1rem;
  padding: 0.4rem 1rem;
  border: 1.5px solid currentColor;
  border-radius: 6px;
  background: transparent;
  color: inherit;
  font: inherit;
  font-weight: 700;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  transition: background-color 0.2s ease, opacity 0.2s ease;
}

.voltar:hover:not(:disabled) {
  background: color-mix(in srgb, currentColor 16%, transparent);
}

.voltar:disabled {
  opacity: 0.6;
  cursor: progress;
}

.erro--visivel {
  flex-basis: 100%;
  padding: 0.6rem 1rem;
  background: #fee2e2;
  color: #991b1b;
  font-weight: 600;
  text-align: center;
  border-bottom: 2px solid #fecaca;
}

:global(.dark) .erro--visivel {
  background: #450a0a;
  color: #fecaca;
  border-bottom: 2px solid #7f1d1d;
}
</style>
